import { keepPreviousData } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export const commissionsRatesQueryKey = ["commissions", "rates"] as const;
export const commissionsRulesQueryKey = ["commissions", "rules"] as const;
export const commissionsEndUsersQueryKey = ["commissions", "end-users"] as const;

export function commissionsFciVolumeQueryKey(repAgencyId: number, calendarYear: number) {
  return ["commissions", "fci-volume", repAgencyId, calendarYear] as const;
}

/** Keeps prior rows visible during filter changes and background refetch. */
export const commissionsStableListQueryOptions = {
  staleTime: 60_000,
  refetchOnWindowFocus: false,
  placeholderData: keepPreviousData,
} as const;

export type CommissionRunRow = {
  id: number;
  run_month: string;
  status: string;
  computed_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  locked_at: string | null;
  locked_by: string | null;
  total_base: string | number;
  total_bonus: string | number;
  total_repless: string | number;
  total_stocking: string | number;
  engine_version: string | null;
  notes: string | null;
  approved_inside_sales_at: string | null;
  approved_inside_sales_by: string | null;
  approved_operations_at: string | null;
  approved_operations_by: string | null;
  approved_executive_at: string | null;
  approved_executive_by: string | null;
  vendor_invoices_created_at: string | null;
  vendor_invoices_created_by: string | null;
  statement_preview_sent_at: string | null;
  statement_preview_sent_by: string | null;
  statements_sent_at: string | null;
  statements_sent_by: string | null;
};

export type CommissionRunApprovalRole = "inside_sales" | "operations" | "executive";

export type CommissionRunLiveSnapshot = {
  totals: {
    totalBase: number;
    totalBonus: number;
    totalRepless: number;
    totalStocking: number;
  };
  validations: RunValidation[];
  blocking: boolean;
  engineVersion: string;
};

export type CommissionRunLineRow = {
  id: number;
  source_kind: string;
  rep_agency_id: number | null;
  rep_agency_name: string | null;
  end_user_name: string | null;
  end_user_fs_account_id: number | null;
  invoice_mv_id: number | null;
  invoice_number: string | null;
  invoice_qb_doc_number: string | null;
  invoice_date: string | null;
  sales_order_number: string | null;
  sales_order_date: string | null;
  sales_order_payment_terms_label: string | null;
  due_date: string | null;
  total_amount: number | null;
  qb_line_total_mismatch: boolean;
  qb_compared_total: number | null;
  qb_line_total_mismatch_reason: string | null;
  fully_paid_date: string | null;
  invoice_end_user_name: string | null;
  basis_amount: string | number;
  effective_rate: string | number | null;
  commission_amount: string | number;
  bonus_year: number | null;
  detail: Record<string, unknown> | null;
};

export type RunValidation = { severity: "error" | "warning"; code: string; message: string };

export function listCommissionRuns() {
  return apiFetch<{ rows: CommissionRunRow[] }>("/api/commissions/runs");
}

export function createCommissionRun(run_month: string, notes?: string) {
  return apiFetch<{ row: CommissionRunRow }>("/api/commissions/runs", {
    method: "POST",
    body: { run_month, notes },
  });
}

export function getCommissionRun(id: number) {
  return apiFetch<{ row: CommissionRunRow; live: CommissionRunLiveSnapshot | null }>(
    `/api/commissions/runs/${id}`,
  );
}

export function listCommissionRunLines(id: number) {
  return apiFetch<{ rows: CommissionRunLineRow[]; live: boolean }>(
    `/api/commissions/runs/${id}/lines`,
  );
}

export function listCommissionRunExcludedItems(id: number) {
  return apiFetch<{ rows: CommissionRunLineRow[] }>(`/api/commissions/runs/${id}/excluded`);
}

export function lockCommissionRun(id: number) {
  return apiFetch<{ ok: boolean; lineCount: number }>(`/api/commissions/runs/${id}/lock`, {
    method: "POST",
  });
}

export function computeCommissionRun(id: number) {
  return apiFetch<{
    ok: boolean;
    totals: Record<string, number>;
    validations: RunValidation[];
    blocking: boolean;
    lineCount: number;
  }>(`/api/commissions/runs/${id}/compute`, { method: "POST" });
}

export function approveCommissionRunRole(id: number, role: CommissionRunApprovalRole) {
  return apiFetch<{ ok: boolean }>(`/api/commissions/runs/${id}/approve-role`, {
    method: "POST",
    body: { role },
  });
}

export function sendCommissionRunStatements(id: number) {
  return apiFetch<{
    dryRun: boolean;
    results: Array<{ id: number; ok: boolean; dryRun?: boolean; error?: string }>;
  }>(`/api/commissions/runs/${id}/send-statements`, { method: "POST" });
}

export type CommissionStatementPreviewUser = {
  id: string;
  display_name: string;
  email: string | null;
};

export function listCommissionStatementPreviewUsers() {
  return apiFetch<{ users: CommissionStatementPreviewUser[] }>(
    "/api/commissions/statement-preview-users",
  );
}

export function sendCommissionRunStatementPreview(id: number, userId: string) {
  return apiFetch<{ ok: boolean; previewEmail: string; statementEmailCount: number }>(
    `/api/commissions/runs/${id}/send-statements-preview`,
    { method: "POST", body: { userId } },
  );
}

export function generateCommissionRunVendorInvoices(id: number) {
  return apiFetch<{
    ok: boolean;
    dryRun: boolean;
    message?: string;
    error?: string;
  }>(`/api/commissions/runs/${id}/generate-vendor-invoices`, { method: "POST" });
}

export function getCommissionBacktest(month: string) {
  return apiFetch<{
    runMonth: string;
    hubTotals: { totalBase: number; totalBonus: number };
    sheetTotals: { totalBase: number; totalBonus: number };
    diffs: Array<{
      repAgencyName: string;
      invoiceNumber: string | null;
      sheetAmount: number;
      hubAmount: number;
      delta: number;
      reason: string;
    }>;
    clean: boolean;
  }>(`/api/commissions/backtest/${month}`);
}

export function listCommissionStatements(runId?: number) {
  const query = runId != null ? `?run_id=${runId}` : "";
  return apiFetch<{ rows: Array<Record<string, unknown>> }>(`/api/commissions/statements${query}`);
}

export function previewCommissionStatement(id: number) {
  return apiFetch<{ html: string }>(`/api/commissions/statements/${id}/preview`);
}

export function sendCommissionStatement(id: number) {
  return apiFetch<{ ok: boolean; dryRun?: boolean }>(`/api/commissions/statements/${id}/send`, {
    method: "POST",
  });
}

export function listCommissionEndUsers() {
  return apiFetch<{
    rows: Array<{
      fs_account_id: number;
      name: string;
      first_sold: string | null;
      date_last_sold: string | null;
      new_end_user_bonus_starts_on: string | null;
      new_end_user_bonus_ends_on: string | null;
      commission_adjustment_rate: number | null;
      commission_adjustment_reason: string | null;
      notes: string | null;
    }>;
  }>("/api/commissions/end-users");
}

export function patchCommissionEndUserProfile(
  fsAccountId: number,
  body: {
    commission_adjustment_rate?: number | null;
    commission_adjustment_reason?: string | null;
  },
) {
  return apiFetch<{ ok: boolean }>(`/api/commissions/end-users/${fsAccountId}`, {
    method: "PATCH",
    body,
  });
}

export type CommissionRuleRow = {
  id: string;
  title: string;
  summary: string;
  detail: string;
  example?: string;
  sourceModule: string;
  manualOverride?: string;
  soldDateFields?: Record<string, string>;
  gapYears?: number;
  windowYears?: number;
  owingLagMonths?: number;
  tierUnitsMaxLow?: number;
  tierUnitsMaxMid?: number;
  shippingSkuExcluded?: string;
  dates?: Record<string, string>;
  formula?: Record<string, string>;
  ranges?: string[];
};

export function getCommissionRules() {
  return apiFetch<{
    engineVersion: string;
    cutoverDryRun: boolean;
    statementsDryRun: boolean;
    rules: CommissionRuleRow[];
  }>("/api/commissions/rules");
}

export type CommissionRateRow = {
  id: number;
  name: string;
  commission_rate: string | number | null;
  commission_rate_tier_0_1000: string | number | null;
  commission_rate_tier_1001_5000: string | number | null;
  commission_rate_tier_5001_plus: string | number | null;
  commission_new_end_user_bonus: boolean;
  commission_new_end_user_bonus_rate: string | number | null;
  commission_terminated_at: string | null;
  prev_year_fci_quantity: number;
  prev_year_bonus_rate: number | null;
  current_year_fci_quantity: number;
  current_year_tentative_bonus_rate: number | null;
};

export function listCommissionRates() {
  return apiFetch<{ rows: CommissionRateRow[] }>("/api/commissions/rates");
}

export function listCommissionAdjustments(runMonth?: string) {
  const query = runMonth != null ? `?run_month=${encodeURIComponent(runMonth)}` : "";
  return apiFetch<{ rows: Array<Record<string, unknown>> }>(`/api/commissions/adjustments${query}`);
}

export function listCommissionStocking() {
  return apiFetch<{ rows: Array<Record<string, unknown>>; totalRemainingValue: number }>(
    "/api/commissions/stocking",
  );
}

export function listPosReports() {
  return apiFetch<{ rows: Array<Record<string, unknown>> }>("/api/commissions/pos-reports");
}

export function getVerificationMonth(month: string) {
  return apiFetch<{ row: Record<string, unknown> | null; computed: Record<string, number> }>(
    `/api/commissions/verification/${month}`,
  );
}

export function listCommissionPayments() {
  return apiFetch<{ rows: Array<Record<string, unknown>> }>("/api/commissions/payments");
}

export type CommissionInvoiceStatus =
  | "not_ready"
  | "ready_for_statement"
  | "on_statement"
  | "vendor_invoice_issued"
  | "vendor_invoice_paid"
  | "zero_dollar_gift";

export type BonusInvoiceStatus = "not_applicable" | CommissionInvoiceStatus;

export type CommissionInvoiceStatusRow = {
  invoice_mv_id: number;
  invoice_number: string;
  invoice_qb_doc_number: string | null;
  invoice_date: string | null;
  sales_order_number: string | null;
  sales_order_date: string | null;
  sales_order_payment_terms_label: string | null;
  due_date: string | null;
  total_amount: number | null;
  qb_line_total_mismatch: boolean;
  qb_compared_total: number | null;
  qb_line_total_mismatch_reason: string | null;
  end_user_fs_account_id: number | null;
  end_user_name: string | null;
  sales_order_year: number | null;
  rep_agency_id: number | null;
  rep_agency_name: string | null;
  fully_paid_date: string | null;
  run_month: string | null;
  run_status: string | null;
  statement_status: string | null;
  commission_status: CommissionInvoiceStatus;
  bonus_run_month: string | null;
  bonus_run_status: string | null;
  bonus_statement_status: string | null;
  bonus_status: BonusInvoiceStatus;
  commission_journal_posted_to_qbo: boolean;
  new_end_user_bonus_historically_in_effect: boolean;
};

export const COMMISSION_INVOICE_STATUS_OPTIONS: Array<{
  value: CommissionInvoiceStatus;
  label: string;
}> = [
  { value: "not_ready", label: "Not ready" },
  { value: "ready_for_statement", label: "Ready for statement" },
  { value: "on_statement", label: "On statement" },
  { value: "vendor_invoice_issued", label: "Vendor invoice issued" },
  { value: "vendor_invoice_paid", label: "Vendor invoice paid" },
  { value: "zero_dollar_gift", label: "$0 invoice" },
];

export const BONUS_INVOICE_STATUS_OPTIONS: Array<{
  value: BonusInvoiceStatus;
  label: string;
}> = [
  { value: "not_applicable", label: "Not applicable" },
  ...COMMISSION_INVOICE_STATUS_OPTIONS,
];

export type CommissionInvoiceStatusSortKey =
  | "invoice_number"
  | "invoice_date"
  | "commission_status"
  | "bonus_status"
  | "end_user_name"
  | "rep_agency_name"
  | "fully_paid_date"
  | "run_month"
  | "sales_order_year"
  | "commission_journal_posted_to_qbo"
  | "new_end_user_bonus_historically_in_effect";

export type CommissionInvoiceStatusSortDir = "asc" | "desc";

export const DEFAULT_COMMISSION_STATUS_FILTER: CommissionInvoiceStatus[] = [
  "not_ready",
  "ready_for_statement",
  "on_statement",
  "vendor_invoice_issued",
];

export const DEFAULT_BONUS_STATUS_FILTER: BonusInvoiceStatus[] = [
  "not_ready",
  "ready_for_statement",
  "on_statement",
  "vendor_invoice_issued",
];

export function listCommissionInvoiceStatuses(options?: {
  commissionStatuses?: CommissionInvoiceStatus[];
  bonusStatuses?: BonusInvoiceStatus[];
  /** @deprecated Use commissionStatuses */
  commissionStatus?: CommissionInvoiceStatus;
  /** @deprecated Use bonusStatuses */
  bonusStatus?: BonusInvoiceStatus;
  /** @deprecated Use commissionStatus */
  status?: CommissionInvoiceStatus;
  search?: string;
  limit?: number;
  offset?: number;
  sortBy?: CommissionInvoiceStatusSortKey;
  sortDir?: CommissionInvoiceStatusSortDir;
}) {
  const params = new URLSearchParams();
  const commissionStatuses =
    options?.commissionStatuses ??
    (options?.commissionStatus != null || options?.status != null
      ? [options.commissionStatus ?? options.status!]
      : undefined);
  if (commissionStatuses != null && commissionStatuses.length > 0) {
    params.set("commission_statuses", commissionStatuses.join(","));
  }
  if (options?.bonusStatuses != null && options.bonusStatuses.length > 0) {
    params.set("bonus_statuses", options.bonusStatuses.join(","));
  } else if (options?.bonusStatus != null) {
    params.set("bonus_status", options.bonusStatus);
  }
  if (options?.search != null && options.search.trim() !== "") {
    params.set("search", options.search.trim());
  }
  if (options?.limit != null) {
    params.set("limit", String(options.limit));
  }
  if (options?.offset != null) {
    params.set("offset", String(options.offset));
  }
  if (options?.sortBy != null) {
    params.set("sort_by", options.sortBy);
  }
  if (options?.sortDir != null) {
    params.set("sort_dir", options.sortDir);
  }
  const query = params.toString();
  return apiFetch<{
    rows: CommissionInvoiceStatusRow[];
    total: number;
    limit: number;
    offset: number;
    sort_by: CommissionInvoiceStatusSortKey;
    sort_dir: CommissionInvoiceStatusSortDir;
    commission_status_counts: Partial<Record<CommissionInvoiceStatus, number>>;
    bonus_status_counts: Partial<Record<BonusInvoiceStatus, number>>;
  }>(`/api/commissions/invoices/status${query === "" ? "" : `?${query}`}`);
}

export function money(value: string | number | null | undefined): string {
  if (value == null || value === "") {
    return "—";
  }
  const n = typeof value === "number" ? value : Number.parseFloat(value);
  if (!Number.isFinite(n)) {
    return String(value);
  }
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}
